import { FirmProduct, ProductAllocationTypes, ProductTypeTypes } from '../types';

function isMainProduct(product: FirmProduct): boolean {
    return (
        product.productTypeType === ProductTypeTypes.Package ||
        product.productTypeType === ProductTypeTypes.Styling
    );
}

function fitsPackage(product: FirmProduct): boolean {
    return (
        !product.isStylingProduct &&
        (product.productTypeType === ProductTypeTypes.Addition ||
            product.productTypeType === ProductTypeTypes.Showcase)
    );
}

function fitsStyling(product: FirmProduct): boolean {
    return product.isStylingProduct && product.productTypeType === ProductTypeTypes.Addition;
}

export function mainProductsOf(products: FirmProduct[]): FirmProduct[] {
    return products.filter(isMainProduct);
}

export function additionsFor(products: FirmProduct[], mainProductId: number | null): FirmProduct[] {
    const mainProduct = products.find((product) => product.id === mainProductId);

    if (!mainProduct || !isMainProduct(mainProduct)) {
        return [];
    }

    const fits =
        mainProduct.productTypeType === ProductTypeTypes.Styling ? fitsStyling : fitsPackage;
    return products.filter(fits);
}

function startsOutChosen(product: FirmProduct): boolean {
    return product.required || product.allocationType.value === ProductAllocationTypes.PreChosen;
}

export function defaultAdditionIds(
    products: FirmProduct[],
    mainProductId: number | null
): number[] {
    return additionsFor(products, mainProductId)
        .filter(startsOutChosen)
        .map((product) => product.id);
}

export function floorPlanProductsOf(products: FirmProduct[]): FirmProduct[] {
    return products.filter((product) => product.productTypeType === ProductTypeTypes.Plan);
}

export function defaultFloorPlanIds(products: FirmProduct[]): number[] {
    return floorPlanProductsOf(products)
        .filter(startsOutChosen)
        .map((product) => product.id);
}

export function floorPlanIdsAfterChoosingAddition(
    products: FirmProduct[],
    additionId: number,
    floorPlanIds: number[]
): number[] {
    const addition = products.find((product) => product.id === additionId);
    const floorPlans = floorPlanProductsOf(products);

    if (!addition || !addition.hasProductPropertyMeasured || floorPlanIds.length > 0) {
        return floorPlanIds;
    }

    return floorPlans.length > 0 ? [floorPlans[0].id] : floorPlanIds;
}

export function arePricesShown(products: FirmProduct[]): boolean {
    return products.length > 0 && products[0].priceBrokerFirm !== null;
}

function priceOf(product: FirmProduct | undefined): number {
    return product && product.priceBrokerFirm ? product.priceBrokerFirm.value : 0;
}

export function orderTotal(
    products: FirmProduct[],
    mainProductId: number | null,
    additionIds: number[],
    floorPlanIds: number[]
): number {
    const mainProduct = products.find((product) => product.id === mainProductId);
    if (!mainProduct) {
        return 0;
    }

    const chosenAdditions = additionsFor(products, mainProductId).filter((product) =>
        additionIds.includes(product.id)
    );
    const chosenFloorPlans = floorPlanProductsOf(products).filter((product) =>
        floorPlanIds.includes(product.id)
    );

    return [...chosenAdditions, ...chosenFloorPlans].reduce(
        (total, product) => total + priceOf(product),
        priceOf(mainProduct)
    );
}
